import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { 
  Search, FileText, Calendar, Truck, MapPin, 
  ArrowLeft, RefreshCcw, ChevronDown, ChevronUp, 
  Info, TrendingUp, Landmark, ShoppingBag
} from 'lucide-react';
import toast from 'react-hot-toast';
import { persistSelectedSiteId, resolvePreferredSiteId } from '../utils/sitePreferences';

/**
 * Visor de Historial de Compras Enterprise
 * Auditoría técnica de abastecimiento y pasivos
 * ADAPTADO AL ESTILO PRODUCTOS
 */
const Compras = ({ onNavigate }) => {
  const { profile } = useAuth();
  const [compras, setCompras] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  // Filtros de Auditoría (Hoy a Hoy por defecto)
  const today = new Date().toISOString().split('T')[0];
  const [fechaDesde, setFechaDesde] = useState(today);
  const [fechaHasta, setFechaHasta] = useState(today);
  const [filtroSitio, setFiltroSitio] = useState('');
  
  // Estado para búsqueda de texto (filtro cliente/proveedor) si se desea implementar en UI
  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    if (profile?.organization_id) {
      loadMasters();
      fetchCompras();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, fechaDesde, fechaHasta]);

  useEffect(() => {
    if (filtroSitio) persistSelectedSiteId(filtroSitio);
  }, [filtroSitio]);

  async function loadMasters() {
    const orgId = profile.organization_id;
    try {
      const { data } = await supabase.from('sitios').select('*').eq('organization_id', orgId).order('nombre');
      const availableSites = data || [];
      setSitios(availableSites);
      setFiltroSitio((prev) =>
        resolvePreferredSiteId(availableSites, {
          currentSiteId: prev,
        }),
      );
    } catch (err) { console.error(err); }
  }

  const fetchCompras = async () => {
    try {
      setLoading(true);
      const start = `${fechaDesde}T00:00:00.000Z`;
      const end = `${fechaHasta}T23:59:59.999Z`;

      const { data, error } = await supabase
        .from('compras')
        .select(`*, proveedores (razon_social), sitios (nombre)`)
        .eq('organization_id', profile.organization_id)
        // Los documentos de pago/recibo (tipo_comprobante='Pago') son contrapartidas en la
        // cuenta corriente, no compras reales: se excluyen del historial de compras.
        .neq('tipo_comprobante', 'Pago')
        .gte('created_at', start)
        .lte('created_at', end)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCompras(data || []);
    } catch (error) {
      toast.error("Error al cargar historial");
    } finally {
      setLoading(false);
    }
  };

  const comprasFiltradas = useMemo(() => {
    let filtered = compras;
    
    // Filtro por sitio
    if (filtroSitio) {
        filtered = filtered.filter(c => c.sitio_id === filtroSitio);
    }

    // Filtro por búsqueda de texto (Proveedor / Nro Comprobante)
    const q = busqueda.toLowerCase().trim();
    if (q) {
        filtered = filtered.filter(c => 
            (c.proveedores?.razon_social || '').toLowerCase().includes(q) ||
            (c.numero_comprobante || '').toLowerCase().includes(q)
        );
    }

    return filtered;
  }, [compras, filtroSitio, busqueda]);

  const stats = useMemo(() => {
    const total = comprasFiltradas.reduce((acc, c) => acc + (Number(c.total_general) || 0), 0);
    const count = comprasFiltradas.length;
    const avg = count > 0 ? total / count : 0;
    return { total, count, avg };
  }, [comprasFiltradas]);

  if (loading && compras.length === 0) return (
    <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
      <RefreshCcw className="animate-spin mb-4 text-[#51A7FD]" size={40} />
      <p className="font-bold uppercase tracking-[0.3em] text-slate-400 text-[10px]">Sincronizando Historial...</p>
    </div>
  );

  return (
    <div className="w-full p-4 bg-[#F8FAFC] min-h-screen font-sans text-slate-700 animate-in fade-in duration-500">
      
      {/* HEADER NUEVO (Estilo Productos) */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 px-1">
        <div className="flex items-center gap-3">
            <button 
                onClick={() => onNavigate('dashboard')} 
                className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] shadow-sm transition-all"
                title="Volver al Dashboard"
            >
                <ArrowLeft size={18} />
            </button>
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Truck size={18} className="text-[#3FF2AA]" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase">Auditoría de Compras</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Registro histórico de ingresos y pasivos
            </p>
          </div>
        </div>

        <button 
            onClick={() => onNavigate('compras-nueva')}
            className="px-5 py-2.5 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
        >
            <Truck size={16} /> Nueva Compra
        </button>
      </div>

      {/* HEADER ANTERIOR (COMENTADO)
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4 px-1">
        <div className="flex items-center gap-3">
          <button onClick={() => onNavigate('dashboard')} className="p-1.5 bg-white border border-slate-200 rounded-lg text-slate-400 hover:text-[#51A7FD] shadow-sm transition-all">
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-lg font-bold text-slate-800 uppercase tracking-tight leading-none">Auditoría de Compras</h1>
            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1 italic">Registro histórico de ingresos y pasivos comerciales</p>
          </div>
        </div>
      </div>
      */}

      {/* KPIS NUEVOS (Estilo Productos) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Inversión Bruta</p>
                <TrendingUp size={16} className="text-slate-300" />
            </div>
            <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">$ {stats.total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total del periodo</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Operaciones</p>
                <Truck size={16} className="text-slate-300" />
            </div>
            <p className="mt-1.5 text-2xl font-black text-emerald-600 tracking-tight">{stats.count}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Compras registradas</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Ticket Promedio</p>
                <Info size={16} className="text-slate-300" />
            </div>
            <p className="mt-1.5 text-2xl font-black text-amber-600 tracking-tight">$ {stats.avg.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Promedio de compra</p>
        </div>
      </div>

      {/* FILTROS NUEVOS (Estilo Productos) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 md:p-4 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2.5">
            {/* BUSCADOR */}
            <div className="md:col-span-4">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                    <Search size={12} /> Buscar
                </label>
                <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                    <input
                        type="text"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        className="w-full pl-11 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase"
                        placeholder="PROVEEDOR, NRO COMPROBANTE..."
                    />
                </div>
            </div>

            {/* FECHAS */}
            <div className="md:col-span-2">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                    <Calendar size={12} /> Desde
                </label>
                <input 
                    type="date" 
                    value={fechaDesde} 
                    onChange={e => setFechaDesde(e.target.value)} 
                    className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner" 
                />
            </div>
            <div className="md:col-span-2">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                    <Calendar size={12} /> Hasta
                </label>
                <input 
                    type="date" 
                    value={fechaHasta} 
                    onChange={e => setFechaHasta(e.target.value)} 
                    className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all shadow-inner" 
                />
            </div>

            {/* SUCURSAL */}
            <div className="md:col-span-3">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
                    <MapPin size={12} /> Sucursal
                </label>
                <select 
                    value={filtroSitio} 
                    onChange={e => setFiltroSitio(e.target.value)} 
                    className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
                >
                    <option value="">TODAS LAS SUCURSALES</option>
                    {sitios.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                </select>
            </div>

            {/* BOTÓN REFRESH */}
            <div className="md:col-span-1 flex items-end">
                <button 
                    onClick={fetchCompras} 
                    className="w-full py-2.5 bg-slate-100 text-slate-500 rounded-xl hover:bg-[#51A7FD] hover:text-white transition-all shadow-sm flex items-center justify-center"
                    title="Actualizar lista"
                >
                    <RefreshCcw size={18} />
                </button>
            </div>
        </div>
      </div>

      {/* FILTROS Y KPIS ANTERIORES (COMENTADO)
      <div className="space-y-3">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl"><TrendingUp size={20}/></div>
            <div>
              <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Inversión Bruta (Periodo)</p>
              <p className="text-lg font-black text-slate-800 tracking-tighter">$ {inversionTotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p>
            </div>
          </div>

          <div className="lg:col-span-3 bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-wrap md:flex-nowrap items-end gap-3">
             ...
          </div>
        </div>
      </div>
      */}

      {/* TABLA DE HISTORIAL (Estilo Productos) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden min-h-[500px] mb-10">
        
        {/* HEADER TABLA */}
        <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
                <Truck size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">
                    Listado de Compras ({comprasFiltradas.length})
                </span>
            </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[900px]">
            <thead>
              <tr className="bg-slate-50/60 text-slate-400 border-b border-slate-100 text-[9px] font-black uppercase tracking-[0.2em]">
                <th className="px-6 py-4">Fecha de Proceso</th>
                <th className="px-6 py-4">Proveedor / Entidad</th>
                <th className="px-6 py-4">Sucursal de Ingreso</th>
                <th className="px-6 py-4 text-right">Monto Total</th>
                <th className="px-6 py-4 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 font-sans">
              {comprasFiltradas.map(c => (
                <React.Fragment key={c.id}>
                  <tr 
                    onClick={() => setExpandedId(expandedId === c.id ? null : c.id)} 
                    className={`transition-all cursor-pointer group ${expandedId === c.id ? 'bg-blue-50/30' : 'hover:bg-blue-50/20'}`}
                  >
                    <td className="px-6 py-4 text-[11px] font-bold text-slate-500">
                      {new Date(c.created_at).toLocaleDateString('es-AR')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Truck size={14} className="text-slate-300" />
                        <span className="font-black text-slate-700 text-[11px] uppercase tracking-tight group-hover:text-[#51A7FD] transition-colors">
                          {c.proveedores?.razon_social || 'Proveedor Desconocido'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-[9px] text-slate-400 font-bold uppercase tracking-widest flex items-center gap-1">
                        <MapPin size={10} className="text-[#51A7FD]" /> {c.sitios?.nombre || 'General'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="text-slate-900 font-black text-[12px] tracking-tight">
                        $ {c.total_general.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {expandedId === c.id ? <ChevronUp size={16} className="text-[#51A7FD]" /> : <ChevronDown size={16} className="text-slate-300 group-hover:text-[#51A7FD]" />}
                    </td>
                  </tr>

                  {/* DETALLE EXPANDIBLE */}
                  {expandedId === c.id && (
                    <tr className="bg-slate-50/30 animate-in fade-in slide-in-from-top-1 duration-200">
                      <td colSpan="5" className="p-4 px-8">
                        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm relative overflow-hidden">
                          <div className="absolute top-0 left-0 w-1 h-full bg-[#51A7FD]" />
                          
                          <div className="flex items-center gap-2 text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-50 pb-2 mb-4">
                            <FileText size={12}/> Auditoría Maestro de Comprobante
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-3">
                              <div>
                                <label className="text-[8px] font-black text-slate-300 uppercase tracking-widest block">Nro. de Comprobante</label>
                                <p className="text-[11px] font-black text-slate-700 uppercase">{c.numero_comprobante || 'SIN REFERENCIA'}</p>
                              </div>
                              <div>
                                <label className="text-[8px] font-black text-slate-300 uppercase tracking-widest block">Observaciones Técnicas</label>
                                <p className="text-[10px] font-bold text-slate-500 uppercase italic leading-relaxed">
                                  {c.observaciones || "SIN DETALLES ADICIONALES REGISTRADOS"}
                                </p>
                              </div>
                            </div>
                            
                            <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 flex items-start gap-3">
                              <Info size={16} className="text-[#51A7FD] shrink-0 mt-0.5" />
                              <div className="space-y-1">
                                <p className="text-[8px] font-black text-blue-600 uppercase tracking-tight leading-none">Imputación Automática</p>
                                <p className="text-[9px] font-bold text-blue-500/80 leading-relaxed uppercase">
                                  Este registro impactó en la Cuenta Corriente del proveedor e incrementó el stock en la sucursal {c.sitios?.nombre}.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {comprasFiltradas.length === 0 && (
                <tr>
                  <td colSpan="5" className="py-24 text-center">
                    <div className="flex flex-col items-center opacity-30">
                      <Landmark size={48} strokeWidth={1} className="mb-2" />
                      <p className="text-[10px] font-black uppercase tracking-[0.3em]">Sin actividad de abastecimiento</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Compras;
